export interface AssistDraftField {
  id: string;
  label: string;
  placeholder: string;
  defaultValue?: string;
  /** When true, field can be left empty (e.g. target-cmd). */
  optional?: boolean;
}

export interface AssistDraftTemplate {
  id: string;
  label: string;
  description: string;
  fields: AssistDraftField[];
  body: string;
}

const GENERIC_ASSIST_BODY = `## Goal
What outcome do you want?

## Context
Paste relevant background.

## Constraints
Budget, tools, tone, deadlines.

## Done when
How will we know it's complete?
`;

const FANOUT_ALL_BODY = `@all {{task}}

## Constraints
{{constraints}}

## Done when
{{doneWhen}}
`;

const BEST_OF_N_BODY = `/best-of-n {{n}} {{task}}

## Constraints
{{constraints}}

## Done when
{{doneWhen}}
`;

const JAISIU_RESEARCH_BODY = `Drive jaisiu-research (JAISIU-2398) — a parallel tree-of-approaches explorer that scores N alternative solution branches with UCB1 (Tree of Thoughts, arXiv:2305.10601).

You are the orchestrator parent session. Read skills/jaisiu-research/SKILL.md end-to-end before starting.

Goal: {{goal}}
Branches (comma-separated, depth-1 children of the root): {{branches}}
Max evaluations: {{maxEvaluations}}
Max depth: {{maxDepth}}
Max branching factor: {{maxBranching}}
Mode: {{mode}} (dry-run is deterministic; live requires a running gateway + real session key)
Top-K to surface: {{topK}}
Output path: {{output}}

Steps:

1. Verify the research package exists: \`ls src/agents/research/\` (must contain explore-tree.ts, tree-runner.ts, live-runner.ts, bridge-fleet-work-plan.ts, index.ts).
2. Run the CLI:
   \`\`\`
   pnpm tsx scripts/jaisiu-research-cli.ts \\
     --goal "{{goal}}" \\
     --branches "{{branches}}" \\
     --max-evaluations {{maxEvaluations}} \\
     --max-depth {{maxDepth}} \\
     --max-branching {{maxBranching}}{{modeFlag}} \\
     --top-k {{topK}} \\
     --output {{output}}
   \`\`\`
   In live mode, \`{{modeFlag}}\` expands to \` --spawn-mode live\` (leading space plus the flag). For dry-run, it expands to an empty string.
3. Inspect \`{{output}}\`:
   - \`summary.winners[]\` — top-K by mean fitness, monotonic descending. First entry is the recommended approach.
   - \`tree.nodes[]\` — every visited node with depth, visits, reward, fitness.
   - \`tree.stats\` — evaluations consumed, nodes visited, max depth reached.
4. If \`mode\` is live, also call \`bridgeToFleetWorkUnits(state)\` from \`src/agents/research/bridge-fleet-work-plan.ts\` and persist the resulting \`FleetWorkUnit[]\` via the existing fleet-work-plan registry (runs \`validateDag\` first; cycles short-circuit).
5. Report the recommended approach + its mean fitness + visit count + the strongest runner-up so the operator can pick consciously.

Hard rules:
- Never claim "research done" from \`pnpm vitest run\` alone — the gate is \`summary.winners.length > 0\` AND \`tree.stats.evaluations === maxEvaluations\` (or \`stoppedReason !== "max-evaluations-reached"\` if it stopped earlier).
- Dry-run is the default for tickets until the operator explicitly opts into live.
- UCB1 c defaults to \`Math.SQRT2\`; do not change it without a written rationale in the ticket.
- If the recommended approach is "no clear winner" (all mean fitness < 0.5), surface that explicitly — do not pretend.

Start now: confirm the package exists, then run the CLI in dry-run unless mode=live.`;

const SEMANTIC_LIFT_BODY = `Orchestrate Semantic Lift 6-Sigma per skills/codebase-translate/SKILL.md.

You are the orchestrator parent session. Read and follow the skill end-to-end.

Project: {{project}}
Source lang: {{sourceLang}} → Target lang: {{targetLang}}
Source dir: {{sourceDir}}
Target dir: {{targetDir}}
Harness: /home/node/jaisiu/skills/codebase-translate/scripts/translation_harness.py

Run the full cycle until skills/codebase-translate/build/{{project}}/translation_report.json has "done": true:

1. oracle — translation_harness.py --mode oracle
2. plan — --mode plan (read build/{{project}}/next_units.json + orchestrator block)
3. spawn — sessions_spawn one subagent per unit (max 4 parallel, {{attempts}} attempts per unit, select max_oracle_pass)
4. wait — do not treat [fleet-complete] as done
5. compile — cmake/Qt on host or SSH (gateway container usually cannot build)
6. gate — --mode gate --heartbeat-file ~/.openclaw/workspace/HEARTBEAT.md{{targetCmdSuffix}}

Hard rules:
- Reply HEARTBEAT_OK only when gate JSON has "done": true
- Never declare done from build alone, manifest counts, or fleet-complete
- On fix-build units: use palace fixes (errsig tags) + prism_memory_write on successful fixes (wing semantic-lift, room {{project}}, hall discoveries)
- Report blockers from translation_report.json gates + sigma.unverifiable_units after each gate

Start now: run --mode oracle, then --mode plan, show next_units.json summary and spawn the first batch.`;

export const ASSIST_DRAFT_TEMPLATES: AssistDraftTemplate[] = [
  {
    id: "generic",
    label: "AI Assist (generic)",
    description: "Goal, context, constraints, and done-when checklist.",
    fields: [],
    body: GENERIC_ASSIST_BODY,
  },
  {
    id: "fanout-all",
    label: "Fan-out (@all)",
    description: "Prefix with @all so every agent gets the same task in parallel.",
    fields: [
      {
        id: "task",
        label: "Task",
        placeholder: "Refactor auth to use the new session store",
      },
      {
        id: "constraints",
        label: "Constraints (optional)",
        placeholder: "No API breaks; keep existing tests green",
        optional: true,
      },
      {
        id: "doneWhen",
        label: "Done when (optional)",
        placeholder: "PR-ready diff + tests pass",
        optional: true,
        defaultValue: "Independent worker results returned; synthesize only after all finish.",
      },
    ],
    body: FANOUT_ALL_BODY,
  },
  {
    id: "best-of-n",
    label: "Best-of-N",
    description: "Spawn N competing attempts via /best-of-n, then pick the strongest.",
    fields: [
      {
        id: "n",
        label: "Attempts (N)",
        placeholder: "3",
        defaultValue: "3",
      },
      {
        id: "task",
        label: "Task",
        placeholder: "Propose a fix for the flaky gateway reconnect",
      },
      {
        id: "constraints",
        label: "Constraints (optional)",
        placeholder: "Prefer minimal diff; no new deps",
        optional: true,
      },
      {
        id: "doneWhen",
        label: "Done when (optional)",
        placeholder: "One clear winner with rationale",
        optional: true,
        defaultValue: "N attempts complete; pick the strongest with a short rationale.",
      },
    ],
    body: BEST_OF_N_BODY,
  },
  {
    id: "semantic-lift",
    label: "Semantic Lift 6-Sigma",
    description: "Orchestrate codebase translation until all gates pass.",
    fields: [
      { id: "project", label: "Project id", placeholder: "npp-qt", defaultValue: "npp-qt" },
      { id: "sourceLang", label: "Source language", placeholder: "cpp", defaultValue: "cpp" },
      { id: "targetLang", label: "Target language", placeholder: "qt6", defaultValue: "qt6" },
      {
        id: "sourceDir",
        label: "Source directory",
        placeholder: "~/.openclaw/workspace/.../PowerEditor/src",
        defaultValue: "~/.openclaw/workspace/notepad-plus-plus-translation/PowerEditor/src",
      },
      {
        id: "targetDir",
        label: "Target directory",
        placeholder: "~/.openclaw/workspace/npp-qt",
        defaultValue: "~/.openclaw/workspace/npp-qt",
      },
      {
        id: "attempts",
        label: "Attempts per unit (best-of-N)",
        placeholder: "8",
        defaultValue: "8",
      },
      {
        id: "targetCmd",
        label: "Gate target command (optional)",
        placeholder: "./build/npp-qt --help",
        optional: true,
      },
    ],
    body: SEMANTIC_LIFT_BODY,
  },
  {
    id: "jaisiu-research",
    label: "jaisiu-research (tree of approaches)",
    description:
      "Steer jaisiu-research — UCB1 tree of N parallel approach branches. Dry-run by default; live opt-in.",
    fields: [
      {
        id: "goal",
        label: "Goal",
        placeholder: "Find the best path to ship JAISIU-2398",
        defaultValue: "Find the best approach for the current ticket",
      },
      {
        id: "branches",
        label: "Branches (comma-separated)",
        placeholder: "A.refactor,B.typescript,C.rust",
        defaultValue: "A.refactor,B.rewrite,C.minimal-patch",
      },
      {
        id: "maxEvaluations",
        label: "Max evaluations",
        placeholder: "9",
        defaultValue: "9",
      },
      {
        id: "maxDepth",
        label: "Max depth",
        placeholder: "2",
        defaultValue: "2",
      },
      {
        id: "maxBranching",
        label: "Max branching factor",
        placeholder: "5",
        defaultValue: "5",
      },
      {
        id: "mode",
        label: "Mode (optional, omit for dry-run)",
        placeholder: "dry-run | live",
        defaultValue: "dry-run",
        optional: true,
      },
      {
        id: "topK",
        label: "Top-K winners to surface",
        placeholder: "3",
        defaultValue: "3",
      },
      {
        id: "output",
        label: "Output JSON path",
        placeholder: "/tmp/jaisiu-research-tree.json",
        defaultValue: "/tmp/jaisiu-research-tree.json",
      },
    ],
    body: JAISIU_RESEARCH_BODY,
  },
];

export function getAssistDraftTemplate(id: string): AssistDraftTemplate | undefined {
  return ASSIST_DRAFT_TEMPLATES.find((t) => t.id === id);
}

export function defaultFieldValues(template: AssistDraftTemplate): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of template.fields) {
    values[field.id] = field.defaultValue ?? "";
  }
  return values;
}

function substitutePlaceholders(body: string, values: Record<string, string>): string {
  const targetCmd = values.targetCmd?.trim() ?? "";
  const mode = (values.mode ?? "").trim();
  const withDerived: Record<string, string> = {
    ...values,
    targetCmdSuffix: targetCmd ? `\n   --target-cmd ${targetCmd}` : "",
    modeFlag: mode === "live" ? " --spawn-mode live" : "",
  };
  return body.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => withDerived[key] ?? "");
}

export function renderAssistDraftTemplate(
  templateId: string,
  fieldValues: Record<string, string> = {},
): string {
  const template = getAssistDraftTemplate(templateId);
  if (!template) {
    return "";
  }
  if (template.fields.length === 0) {
    return template.body;
  }
  const merged = { ...defaultFieldValues(template), ...fieldValues };
  for (const field of template.fields) {
    if (!field.optional && !merged[field.id]?.trim()) {
      throw new Error(`Missing required field: ${field.label}`);
    }
  }
  return substitutePlaceholders(template.body, merged);
}

export function mergeAssistDraftIntoComposer(existingDraft: string, inserted: string): string {
  const base = existingDraft.trim();
  return base ? `${base}\n\n${inserted}` : inserted;
}
